// Copyright (c) 2022. Heusala Group Oy <info@heusalagroup.fi>. All rights reserved.

import { NavLink } from "react-router-dom";
import { PRODUCT_ARTICLE_CLASS_NAME } from "../../../../core/constants/className";
import { ComponentUtils, ComponentNodeMap } from "../../../../../../io/hyperify/frontend/components/utils/ComponentUtils";
import { BuyButton } from "../buyButton/BuyButton";
import { Product } from "../../../../../../io/hyperify/core/store/types/product/Product";
import { ProductPrice } from "../../../../../../io/hyperify/core/store/types/product/ProductPrice";
import { ReactNode } from "react";
import { TranslationParams } from "../../../../../../io/hyperify/core/types/TranslationParams";
import { startsWith } from "../../../../../../io/hyperify/core/functions/startsWith";
import { TranslationFunction } from "../../../../../../io/hyperify/core/types/TranslationFunction";
import "./ProductArticle.scss";

export enum ProductArticleChildrenPlacement {
    TOP,
    BEFORE_BUTTONS,
    BOTTOM
}

export interface ProductArticleProps {

    readonly t: TranslationFunction;
    readonly translationParams ?: TranslationParams;
    readonly className?: string;
    readonly title?: string;

    /**
     * @TODO Must be changed to children prop style
     */
    readonly description?: readonly any[];
    readonly previewImage?: string;
    readonly primaryButtonTo?: string;
    readonly children?: ReactNode;
    readonly childrenPlacement?: ProductArticleChildrenPlacement;
    readonly primaryButtonText?: string;
    readonly secondaryButtonTo?: string;
    readonly secondaryButtonText?: string;

    readonly paramBlocks ?: ComponentNodeMap;

    readonly buyButtonText    ?: string;
    readonly buyButtonProduct ?: Product;
    readonly buyButtonPrice   ?: ProductPrice;

}

/**
 * @param props
 */
export function ProductArticle (props: ProductArticleProps) {
    const t = props?.t;
    const className: string | undefined = props?.className;
    const translationParams = props?.translationParams;
    const title = props?.title;
    const description = props?.description;
    const childrenPlacement = props?.childrenPlacement ?? ProductArticleChildrenPlacement.BOTTOM;
    const previewImage = props?.previewImage;
    const paramBlocks = props?.paramBlocks;
    const buyButtonProduct = props?.buyButtonProduct;
    const buyButtonPrice = props?.buyButtonPrice;
    const buyButtonText = props?.buyButtonText;
    const primaryButtonTo = props?.primaryButtonTo;
    const primaryButtonText = props?.primaryButtonText;
    const children = props?.children;
    const isPrimaryLink: boolean = !!(primaryButtonTo && startsWith(primaryButtonTo, 'https://'));

    const secondaryButtonTo = props?.secondaryButtonTo;
    const secondaryButtonText = props?.secondaryButtonText;
    const isSecondaryLink: boolean = !!(secondaryButtonTo && startsWith(secondaryButtonTo, 'https://'));

    return (
        <div className={`${
            PRODUCT_ARTICLE_CLASS_NAME
            + (className ? ' '+className : '')
        }`}>
            <img
                className={`${PRODUCT_ARTICLE_CLASS_NAME}-image`}
                src={previewImage}
                alt={""}
            />
            <div className={`${PRODUCT_ARTICLE_CLASS_NAME}-text`}>

                {children && childrenPlacement === ProductArticleChildrenPlacement.TOP ? children : null}

                <h2>{title}</h2>

                {ComponentUtils.prepareParagraphNodes(t, description, paramBlocks, translationParams)}

                <div className={`${PRODUCT_ARTICLE_CLASS_NAME}-buttons`}>

                    {children && childrenPlacement === ProductArticleChildrenPlacement.BEFORE_BUTTONS ? children : null}

                    {buyButtonProduct && buyButtonPrice ? (
                        <BuyButton
                            className={`${PRODUCT_ARTICLE_CLASS_NAME}-buttons-button ${PRODUCT_ARTICLE_CLASS_NAME}-buy-button`}
                            product={buyButtonProduct}
                            price={buyButtonPrice}
                        >{t(`${buyButtonText}`, translationParams) ?? 'no text'}</BuyButton>
                    ) : null}

                    {primaryButtonTo ? (
                        isPrimaryLink ? (
                            <a
                                href={primaryButtonTo}
                                className={`${PRODUCT_ARTICLE_CLASS_NAME}-buttons-button ${PRODUCT_ARTICLE_CLASS_NAME}-primary-button`}
                            >{primaryButtonText ?? 'no text'}</a>
                        ) : (
                            <NavLink
                                to={primaryButtonTo}
                                className={`${PRODUCT_ARTICLE_CLASS_NAME}-buttons-button ${PRODUCT_ARTICLE_CLASS_NAME}-primary-button`}
                            >{primaryButtonText ?? 'no text'}</NavLink>
                        )
                    ) : null}

                    {secondaryButtonTo ? (
                        isSecondaryLink ? (
                            <a
                                href={secondaryButtonTo}
                                className={`${PRODUCT_ARTICLE_CLASS_NAME}-buttons-button ${PRODUCT_ARTICLE_CLASS_NAME}-secondary-button`}
                            >{secondaryButtonText ?? 'no text'}</a>
                        ) : (
                            <NavLink
                                to={secondaryButtonTo}
                                className={`${PRODUCT_ARTICLE_CLASS_NAME}-buttons-button ${PRODUCT_ARTICLE_CLASS_NAME}-secondary-button`}
                            >{secondaryButtonText ?? 'no text'}</NavLink>
                        )
                    ) : null}

                    {children && childrenPlacement === ProductArticleChildrenPlacement.BOTTOM ? children : null}

                </div>

            </div>
        </div>
    );
}
